<template>
  <b-modal v-model="isShown" centered size="lg" hide-header hide-footer body-class="p-0">
    <div class="be-facilities-modal">
      <div class="be-facilities-modal__head">
        <div>
          <p class="be-facilities-modal__title">{{ $t('general.facilities') }}</p>
          <p class="be-facilities-modal__subtitle">{{ $t('general.available-facilities') }}</p>
        </div>
        <button type="button" class="be-facilities-modal__close" @click="$emit('close')">
          <X :size="18" />
        </button>
      </div>
      <div class="be-facilities-modal__body">
        <div
          v-for="category in categories"
          :key="category.id"
          class="be-facilities-modal__group">
          <button
            v-if="category.name"
            type="button"
            class="be-facilities-modal__group-head"
            @click="toggleCategory(category.id)">
            <span class="be-facilities-modal__group-name">{{ category.name }}</span>
            <ChevronDown
              :size="20"
              class="be-facilities-modal__group-chevron"
              :class="{ 'be-facilities-modal__group-chevron--collapsed': isCategoryCollapsed(category.id) }" />
          </button>
          <div v-show="!isCategoryCollapsed(category.id)" class="be-facilities-modal__grid">
            <div
              v-for="facility in category.facility"
              :key="facility.id"
              class="be-facilities-modal__item">
              <span
                v-if="facility.svg_tag"
                class="be-facilities-modal__icon"
                v-html="facility.svg_tag"></span>
              <img
                v-else-if="facility.image_url"
                :src="facility.image_url"
                :alt="facility.name"
                class="be-facilities-modal__icon" />
              <!-- keeps every name on the same left edge when an entry has no artwork -->
              <span v-else class="be-facilities-modal__icon"></span>
              <p class="be-facilities-modal__name">{{ facility.name }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { X, ChevronDown } from 'lucide-vue';

export default {
  name: 'BeFacilitiesModal',
  components: {
    X,
    ChevronDown,
  },
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    categories: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      // holds the closed ones, so categories arriving later default to open
      collapsedCategoryIds: [],
    };
  },
  computed: {
    isShown: {
      get() {
        return this.isOpen;
      },
      set(isShown) {
        if (!isShown) this.$emit('close');
      },
    },
  },
  watch: {
    isOpen(isOpen) {
      if (isOpen) this.collapsedCategoryIds = [];
    },
  },
  methods: {
    isCategoryCollapsed(categoryId) {
      return this.collapsedCategoryIds.indexOf(categoryId) !== -1;
    },
    toggleCategory(categoryId) {
      const index = this.collapsedCategoryIds.indexOf(categoryId);
      if (index === -1) {
        this.collapsedCategoryIds.push(categoryId);
        return;
      }
      this.collapsedCategoryIds.splice(index, 1);
    },
  },
};
</script>

<style scoped>
/* modal is portaled to <body>, outside .be-landing — restate the font family */
.be-facilities-modal {
  font-family: var(--be-v3-content-font, BeV3Sans), sans-serif;
}
.be-facilities-modal__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 24px;
  border-bottom: 1px solid #e8e8e8;
}
.be-facilities-modal__title {
  margin: 0 0 4px;
  font-family: var(--be-v3-title-font, BeV3Sans), sans-serif;
  font-size: 1.375rem;
  font-weight: 600;
  color: #1a1a2e;
}
.be-facilities-modal__subtitle {
  margin: 0;
  font-size: 0.875rem;
  color: #777;
}
.be-facilities-modal__close {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: #fff;
  border: 1px solid #d5d9e0;
  border-radius: 12px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  cursor: pointer;
}
.be-facilities-modal__body {
  padding: 24px;
  max-height: 60vh;
  overflow-y: auto;
}
.be-facilities-modal__group + .be-facilities-modal__group {
  margin-top: 32px;
}
.be-facilities-modal__group-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  margin-bottom: 16px;
  padding: 0;
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
}
.be-facilities-modal__group-name {
  font-family: var(--be-v3-title-font, BeV3Sans), sans-serif;
  font-size: 1.125rem;
  font-weight: 700;
  color: #1a1a2e;
}
.be-facilities-modal__group-chevron {
  flex-shrink: 0;
  transition: transform var(--be-v3-dur-fast, 150ms) var(--be-v3-ease, ease-out);
}
.be-facilities-modal__group-chevron--collapsed {
  transform: rotate(-180deg);
}
.be-facilities-modal__grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}
@media (max-width: 575.98px) {
  .be-facilities-modal__grid {
    grid-template-columns: 1fr;
  }
}
.be-facilities-modal__item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px 20px;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: var(--be-v3-radius, 14px);
}
.be-facilities-modal__icon {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  object-fit: contain;
}
.be-facilities-modal__icon >>> svg {
  width: 100%;
  height: 100%;
}
.be-facilities-modal__name {
  margin: 0;
  font-size: 1rem;
  color: #1a1a2e;
}
</style>



// WEBPACK FOOTER //
// src/components/hotel/be/v3/BeFacilitiesModal.vue